import { useState, useEffect, useRef, useCallback } from 'react';
import { Phone, MessageSquare, Mail, Linkedin, Calendar, Video } from 'lucide-react';
import { SettingsLayout } from './SettingsLayout';
import { ProfileSettings } from './ProfileSettings';
import { SalesMaterialsSettings } from './SalesMaterialsSettings';
import { CallAgentSettings } from './CallAgentSettings';
import { WhatsAppAgentSettings } from './WhatsAppAgentSettings';
import { EmailAgentSettings } from './EmailAgentSettings';
import { LinkedInAgentSettings } from './LinkedInAgentSettings';
import { PhoneNumbersConnectionCard } from './PhoneNumbersConnectionCard';
import { WhatsAppConnectionCard } from './WhatsAppConnectionCard';
import { SalesTeamSection } from './SalesTeamSection';
import { LinkedInConnectionCard } from './LinkedInConnectionCard';
import { VideoCallsSection } from './VideoCallsSection';
import { CalendarsSection } from './CalendarsSection';
import { MemberVideoSection } from './MemberVideoSection';
import { EmailsConnectionSection } from './EmailsConnectionSection';
import { SubscriptionSettings } from './SubscriptionSettings';
import { UsageLimitsSettings } from './UsageLimitsSettings';
import { PaymentMethodSettings } from './PaymentMethodSettings';
import { TransactionsSettings } from './TransactionsSettings';
import { UnsavedChangesModal } from './UnsavedChangesModal';
import { useAuth } from '../../contexts/AuthContext';
import { canSeeBilling, isAdmin, isMember } from '../../utils/permissions';

const BILLING_SECTIONS = ['subscription', 'usage-limits', 'payment-method', 'transactions'];
const HIDDEN_SECTIONS = ['payment-method', 'transactions'];

const SettingsPage = () => {
  const { user } = useAuth();
  const showBilling = canSeeBilling(user);
  const [activeSection, setActiveSection] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return 'profile';
    return hash;
  });

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingSection, setPendingSection] = useState<string | null>(null);
  const [savingFromModal, setSavingFromModal] = useState(false);
  const saveRef = useRef<(() => Promise<void>) | null>(null);
  const discardRef = useRef<(() => void) | null>(null);

  const handleSectionChange = useCallback((section: string) => {
    if (section === activeSection) return;
    if (hasUnsavedChanges) {
      setPendingSection(section);
      return;
    }
    setActiveSection(section);
  }, [activeSection, hasUnsavedChanges]);

  const handleModalSave = async () => {
    if (saveRef.current) {
      setSavingFromModal(true);
      await saveRef.current();
      setSavingFromModal(false);
    }
    if (pendingSection) {
      setActiveSection(pendingSection);
      setPendingSection(null);
    }
  };

  const handleModalDiscard = () => {
    if (discardRef.current) {
      discardRef.current();
    }
    if (pendingSection) {
      setActiveSection(pendingSection);
      setPendingSection(null);
    }
  };

  const handleModalCancel = () => {
    setPendingSection(null);
  };

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash && hash !== activeSection) {
        if (hasUnsavedChanges) {
          window.history.replaceState(null, '', `#${activeSection}`);
          setPendingSection(hash);
          return;
        }
        setActiveSection(hash);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [activeSection, hasUnsavedChanges]);

  useEffect(() => {
    if (activeSection) {
      window.location.hash = activeSection;
    }
  }, [activeSection]);

  const renderSection = () => {
    if (!showBilling && BILLING_SECTIONS.includes(activeSection)) {
      return <ProfileSettings />;
    }

    if (HIDDEN_SECTIONS.includes(activeSection)) {
      return <SubscriptionSettings />;
    }

    switch (activeSection) {
      case 'profile':
        return <ProfileSettings />;
      case 'sales-playbook':
        return (
          <SalesMaterialsSettings
            onDirtyStateChange={setHasUnsavedChanges}
            saveRef={saveRef}
            discardRef={discardRef}
          />
        );
      case 'call-agent':
        return (
          <CallAgentSettings
            onDirtyStateChange={setHasUnsavedChanges}
            saveRef={saveRef}
            discardRef={discardRef}
          />
        );
      case 'whatsapp-agent':
        return (
          <WhatsAppAgentSettings
            onDirtyStateChange={setHasUnsavedChanges}
            saveRef={saveRef}
            discardRef={discardRef}
          />
        );
      case 'email-agent':
        return (
          <EmailAgentSettings
            onDirtyStateChange={setHasUnsavedChanges}
            saveRef={saveRef}
            discardRef={discardRef}
          />
        );
      case 'linkedin-agent':
        return (
          <LinkedInAgentSettings
            onDirtyStateChange={setHasUnsavedChanges}
            saveRef={saveRef}
            discardRef={discardRef}
          />
        );
      case 'phone-numbers':
        return (
          <div className="max-w-full space-y-6">
            <div className="mb-6 flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl flex items-center justify-center shadow-md">
                <Phone className="w-6 h-6 text-[#1875FF]" />
              </div>
              <div>
                <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display">Phone Numbers</h2>
                <p className="text-gray-600">
                  Manage your connected phone numbers for call campaigns
                </p>
              </div>
            </div>
            <PhoneNumbersConnectionCard />
          </div>
        );
      case 'whatsapp':
        return (
          <div className="max-w-full space-y-6">
            <div className="mb-6 flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl flex items-center justify-center shadow-md">
                <MessageSquare className="w-6 h-6 text-[#1875FF]" />
              </div>
              <div>
                <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display">WhatsApp</h2>
                <p className="text-gray-600">
                  Manage your WhatsApp business accounts for automated outreach
                </p>
              </div>
            </div>
            <WhatsAppConnectionCard />
          </div>
        );
      case 'emails':
        return (
          <div className="max-w-full space-y-6">
            <div className="mb-6 flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl flex items-center justify-center shadow-md">
                <Mail className="w-6 h-6 text-[#1875FF]" />
              </div>
              <div>
                <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display">Emails</h2>
                <p className="text-gray-600">
                  Connect your email accounts for sending and receiving emails via SalesPad
                </p>
              </div>
            </div>
            <EmailsConnectionSection />
          </div>
        );
      case 'calendar':
        return (
          <div className="max-w-full space-y-6">
            <div className="mb-6 flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl flex items-center justify-center shadow-md">
                <Calendar className="w-6 h-6 text-[#1875FF]" />
              </div>
              <div>
                <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display">
                  {isAdmin(user) ? 'Calendar Connections' : 'Calendar Connection'}
                </h2>
                <p className="text-gray-600">
                  {isAdmin(user)
                    ? 'Manage your sales team calendars and availability for smart meeting routing'
                    : 'Connect your calendar so leads can book meetings directly on your schedule'}
                </p>
              </div>
            </div>
            {isAdmin(user) ? <SalesTeamSection /> : <CalendarsSection />}
          </div>
        );
      case 'linkedin':
        return (
          <div className="max-w-full space-y-6">
            <div className="mb-6 flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl flex items-center justify-center shadow-md">
                <Linkedin className="w-6 h-6 text-[#1875FF]" />
              </div>
              <div>
                <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display">LinkedIn</h2>
                <p className="text-gray-600">
                  Connect your LinkedIn account for automated outreach
                </p>
              </div>
            </div>
            <LinkedInConnectionCard />
          </div>
        );
      case 'video-calls':
        return (
          <div className="max-w-full space-y-6">
            <div className="mb-6 flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl flex items-center justify-center shadow-md">
                <Video className="w-6 h-6 text-[#1875FF]" />
              </div>
              <div>
                <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display">
                  Video Calls
                </h2>
                <p className="text-gray-600">
                  {isAdmin(user)
                    ? 'Connect your preferred video conferencing platform for automated meeting scheduling'
                    : 'Connect your video provider for meetings booked on your calendar'}
                </p>
              </div>
            </div>
            {isAdmin(user) ? <VideoCallsSection /> : <MemberVideoSection />}
          </div>
        );
      case 'subscription':
        return <SubscriptionSettings />;
      case 'usage-limits':
        return <UsageLimitsSettings onSectionChange={handleSectionChange} />;
      case 'payment-method':
        return <PaymentMethodSettings />;
      case 'transactions':
        return <TransactionsSettings />;
      default:
        return <ProfileSettings />;
    }
  };

  return (
    <>
      <SettingsLayout activeSection={activeSection} onSectionChange={handleSectionChange}>
        {renderSection()}
      </SettingsLayout>
      <UnsavedChangesModal
        isOpen={pendingSection !== null}
        saving={savingFromModal}
        onSave={handleModalSave}
        onDiscard={handleModalDiscard}
        onCancel={handleModalCancel}
      />
    </>
  );
};

export default SettingsPage;
